import { z } from "zod";

/**
 * Enum for State Codes
 */
export const State = {
  AmericanSamoa: "AS",
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  DistrictOfColumbia: "DC",
  Florida: "FL",
  Georgia: "GA",
  Guam: "GU",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  NorthernMarianaIslands: "MP",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  NewHampshire: "NH",
  NewJersey: "NJ",
  NewMexico: "NM",
  NewYork: "NY",
  NorthCarolina: "NC",
  NorthDakota: "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  PuertoRico: "PR",
  RhodeIsland: "RI",
  SouthCarolina: "SC",
  SouthDakota: "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  VirginIslands: "VI",
  Washington: "WA",
  WestVirginia: "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  UNKNOWN: "UNKNOWN",
} as const;

export const StateSchema = z.enum(State);

export type StateType = z.infer<typeof StateSchema>;

// Mirror the read-extension fallback on write so the raw column equals every read (and recorded history).
export const normalizeStateCode = (stateCode: string): StateType => {
  const result = StateSchema.safeParse(stateCode as unknown);
  return result.success ? result.data : State.UNKNOWN;
};

/**
 * Use this if you need to loop through all the options
 */
export const StateValues = Object.values(State);

export const StateDisplayNames: Record<StateType, string> = {
  [State.AmericanSamoa]: "American Samoa",
  [State.Alabama]: "Alabama",
  [State.Alaska]: "Alaska",
  [State.Arizona]: "Arizona",
  [State.Arkansas]: "Arkansas",
  [State.California]: "California",
  [State.Colorado]: "Colorado",
  [State.Connecticut]: "Connecticut",
  [State.Delaware]: "Delaware",
  [State.DistrictOfColumbia]: "District of Columbia",
  [State.Florida]: "Florida",
  [State.Georgia]: "Georgia",
  [State.Guam]: "Guam",
  [State.Hawaii]: "Hawaii",
  [State.Idaho]: "Idaho",
  [State.Illinois]: "Illinois",
  [State.Indiana]: "Indiana",
  [State.Iowa]: "Iowa",
  [State.Kansas]: "Kansas",
  [State.Kentucky]: "Kentucky",
  [State.Louisiana]: "Louisiana",
  [State.NorthernMarianaIslands]: "Northern Mariana Islands",
  [State.Maine]: "Maine",
  [State.Maryland]: "Maryland",
  [State.Massachusetts]: "Massachusetts",
  [State.Michigan]: "Michigan",
  [State.Minnesota]: "Minnesota",
  [State.Mississippi]: "Mississippi",
  [State.Missouri]: "Missouri",
  [State.Montana]: "Montana",
  [State.Nebraska]: "Nebraska",
  [State.Nevada]: "Nevada",
  [State.NewHampshire]: "New Hampshire",
  [State.NewJersey]: "New Jersey",
  [State.NewMexico]: "New Mexico",
  [State.NewYork]: "New York",
  [State.NorthCarolina]: "North Carolina",
  [State.NorthDakota]: "North Dakota",
  [State.Ohio]: "Ohio",
  [State.Oklahoma]: "Oklahoma",
  [State.Oregon]: "Oregon",
  [State.Pennsylvania]: "Pennsylvania",
  [State.PuertoRico]: "Puerto Rico",
  [State.RhodeIsland]: "Rhode Island",
  [State.SouthCarolina]: "South Carolina",
  [State.SouthDakota]: "South Dakota",
  [State.Tennessee]: "Tennessee",
  [State.Texas]: "Texas",
  [State.Utah]: "Utah",
  [State.Vermont]: "Vermont",
  [State.Virginia]: "Virginia",
  [State.VirginIslands]: "Virgin Islands",
  [State.Washington]: "Washington",
  [State.WestVirginia]: "West Virginia",
  [State.Wisconsin]: "Wisconsin",
  [State.Wyoming]: "Wyoming",
  [State.UNKNOWN]: "Unknown",
};

/**
 * States where Remi is not licensed and cannot perform work
 */
export const UnlicensedStates: StateType[] = [
  State.Alaska,
  State.Hawaii,
  State.NewYork,
  State.VirginIslands,
  State.AmericanSamoa,
  State.Guam,
  State.NorthernMarianaIslands,
  State.PuertoRico,
  State.UNKNOWN,
];
